import { useContext } from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { getSingleParam } from '@confluence/route-manager-utils/entry-points/getSingleParam.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';

import { WebPanelLocationQuery } from './WebPanelLocation.tsx';
import type {
	WebPanelLocationQuery$data,
	WebPanelLocationQuery$variables,
} from './__generated__/WebPanelLocationQuery.graphql.ts';
import type { WebPanel } from './WebPanelLocation.tsx';

export const useWebPanelLocation = ({
	spaceKey,
	contentId,
	location,
	// Query is preloaded on the server-side. reused the preloaded data.
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	fetchPolicy = process.env.REACT_SSR ? 'cache-first' : 'cache-and-network',
	onCompleted,
}: {
	spaceKey?: string;
	contentId?: string;
	location: string;
	fetchPolicy?: 'cache-first' | 'cache-and-network' | 'network-only';
	onCompleted?: () => void;
}) => {
	const { getQueryParams } = useContext(RoutesContext);
	const queryParams = getQueryParams();

	const spaceIdentifier =
		spaceKey || getSingleParam(queryParams, 'spaceKey') || getSingleParam(queryParams, 'key');
	const contentIdentifier = contentId || getSingleParam(queryParams, 'pageId');

	const variables = contentIdentifier
		? { contentId: contentIdentifier, location }
		: { spaceKey: spaceIdentifier, location };

	const baseOptions = {
		variables,
		...(onCompleted && { onCompleted }),
	};

	const { loading, data, error } = expValEquals(
		'confluence_stop_watching_webpanellocationquery',
		'isEnabled',
		true,
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryNoFurtherUpdateInterop<WebPanelLocationQuery$data, WebPanelLocationQuery$variables>(
				WebPanelLocationQuery,
				baseOptions,
			)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryInterop<WebPanelLocationQuery$data, WebPanelLocationQuery$variables>(
				WebPanelLocationQuery,
				{
					...baseOptions,
					fetchPolicy,
				},
			);

	const panels = (data?.webPanels || []).filter((panel) => panel) as WebPanel[];

	return {
		panels,
		loading,
		error,
	};
};
